import React from 'react';
import { PixelStreamingCommunicator } from '../sdk/PixelStreamingCommunicator';
import { PixelStreamingHandler } from '../sdk/PixelStreamingHandler';
// import './TopBar.css'; // Temporarily disabled - styles moved to UILayerComponent.css

// Import existing components that we'll reuse
interface TopBarProps {
  viewMode: 'exterior' | 'interior';
  rotation: number;
  communicator: PixelStreamingCommunicator;
  handler: PixelStreamingHandler | null;
  onBackToExterior: () => void;
  children?: React.ReactNode; // Add children prop for ContextBlock
  isMarketingMode?: boolean;
  onToggleMarketingMode?: () => void;
}

// Logo Component
const TopBarLogo: React.FC = () => {
  return (
    <div className="topbar-logo">
      <img 
        src="https://www.postpark-winsen.de/wp-content/uploads/2025/04/Postpark-Logo-Light.svg" 
        alt="Post Park Winsen Logo" 
        className="topbar-logo__image"
      />
    </div>
  );
};

// Compass Component (reused from existing)
const TopBarCompass: React.FC<{ rotation: number }> = ({ rotation }) => {
  const degrees = Math.round((360 - (rotation * 360 + 180) + 15) % 360);
  
  return (
    <div className="topbar-compass">
      <div className="compass-rose" style={{ transform: `rotate(${degrees}deg)` }}>
        <div className="compass-direction">N</div>
        <div className="compass-direction">E</div>
        <div className="compass-direction">S</div>
        <div className="compass-direction">W</div>
      </div>
    </div>
  );
};

// Back Button Component (reused from existing)
const TopBarBackButton: React.FC<{ 
  communicator: PixelStreamingCommunicator;
  onBackToExterior: () => void;
}> = ({ communicator, onBackToExterior }) => {
  return (
    <button 
      className="topbar-back-button" 
      onClick={() => {
        communicator.goBackToExterior();
        onBackToExterior();
      }}
      title="Back to Exterior"
    >
      ← Back
    </button>
  );
};

// Camera Button Component (reused from existing)
const TopBarCameraButton: React.FC<{ handler: PixelStreamingHandler | null }> = ({ handler }) => {
  const captureFrame = () => {
    if (!handler) return;

    const videoElement = handler.getVideoElement();
    if (!videoElement) {
      console.warn('Video element not found');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.warn('Could not get canvas context');
      return;
    }
    
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob((blob) => {
      if (!blob) {
        console.warn('Could not create blob from canvas');
        return;
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `screenshot-${new Date().toISOString()}.png`;
      document.body.appendChild(a);
      a.click();
      try {
        document.body.removeChild(a);
      } catch {
        // Element may already be removed
      }
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <button 
      className="topbar-camera-button"
      onClick={captureFrame}
      title="Take Screenshot"
    >
      📸
    </button>
  );
};

// Profile Button Component
const TopBarProfileButton: React.FC<{
  isMarketingMode: boolean;
  onToggle: () => void;
}> = ({ isMarketingMode, onToggle }) => {
  return (
    <button
      className={`topbar-profile-button ${isMarketingMode ? 'marketing-mode-active' : ''}`}
      onClick={onToggle}
      title={isMarketingMode ? "Exit Marketing Mode" : "Marketing Mode"}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    </button>
  );
};

// Main TopBar Component
export const TopBar: React.FC<TopBarProps> = ({
  viewMode,
  rotation,
  communicator,
  handler,
  onBackToExterior,
  children, // Accept children
  isMarketingMode = false,
  onToggleMarketingMode = () => {}
}) => {
  return (
    <div className="topbar">
      {/* Left Section */}
      <div className="topbar-section topbar-section--left">
        <TopBarLogo />
        {!isMarketingMode && viewMode === 'exterior' && <TopBarCompass rotation={rotation} />}
        {!isMarketingMode && viewMode === 'interior' && (
          <TopBarBackButton
            communicator={communicator}
            onBackToExterior={onBackToExterior}
          />
        )}
      </div>

      {/* Center Section - Now renders children (ContextBlock) - Hidden in marketing mode */}
      {!isMarketingMode && (
        <div className="topbar-section topbar-section--center">
          {children}
        </div>
      )}

      {/* Right Section */}
      <div className="topbar-section topbar-section--right">
        {!isMarketingMode && <TopBarCameraButton handler={handler} />}
        <TopBarProfileButton
          isMarketingMode={isMarketingMode}
          onToggle={onToggleMarketingMode}
        />
      </div>
    </div>
  );
}; 